import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { NumericInput, Slider, Spinner, Intent, Button as BPButton } from '@blueprintjs/core'
import { Button } from 'components/Button'
import { Colors } from 'design/colors'
import { useDevices } from 'hooks/useDevices'
import { usePlayback } from 'hooks/usePlayback'
import { packetThreadPoolApi } from 'utils/packet-workers'
import { throttle, filenameWithTimestamp, downloadText } from 'utils/common'
import { hidSend } from 'connections/hid'
import { getPositionUnitLabel } from 'hooks/useThrottleConfig'
import {
  ControlMode,
  InputMode,
  serializeSetUsbThrottleControlRequest,
  serializeStartSysidRequest,
  serializeStopSysidRequest
} from 'utils/common-types'
import {
  INavigationProps,
  FORM_WIDTH,
  configContainerStyle,
  screenTitleStyle
} from './CStoreEditor'
import { useSubActuators } from 'hooks/useSubActuators'

const REFRESH_MS = 50
const PULSE_INTERVAL_MS = 50
const PLOT_WIDTH = 500
const PLOT_HEIGHT = 250
const PLOT_PADDING = { top: 20, right: 20, bottom: 40, left: 60 }

interface SysIDTelemetry {
  excitationFrequency: number | undefined
  closedLoopMagnitude: number | undefined
  closedLoopPhase: number | undefined
  openLoopMagnitude: number | undefined
  openLoopPhase: number | undefined
  motorSpeed: number | undefined
  motorPosition: number | undefined
}

interface FrequencyDataPoint {
  frequency: number
  closedLoopMagnitude: number
  closedLoopPhase: number
  openLoopMagnitude: number
  openLoopPhase: number
  timestamp: number
}

const toNumber = (val: number | boolean | null): number | undefined => {
  if (val === null || typeof val === 'boolean') return undefined
  return val
}

// SysID telemetry rides on REALTIME_DATA (board-level) — only one motor at a
// time runs SysID, so the firmware overlays values into shared fields:
//   sysidFrequency  -> excitation frequency (Hz)
//   sysidMagnitude  -> closed-loop magnitude
//   motorCurrent    -> closed-loop phase (deg)
//   batteryVoltage  -> open-loop magnitude
//   batteryCurrent  -> open-loop phase (deg)
// Position/speed come from MULTI_MOTOR_TELEMETRY (per-motor) so they track
// whichever motor the user is commanding.
const useSysIDTelemetry = (motorIndex?: number): SysIDTelemetry => {
  const { timeWindow } = usePlayback()
  const [telemetry, setTelemetry] = useState<SysIDTelemetry>({
    excitationFrequency: undefined,
    closedLoopMagnitude: undefined,
    closedLoopPhase: undefined,
    openLoopMagnitude: undefined,
    openLoopPhase: undefined,
    motorSpeed: undefined,
    motorPosition: undefined
  })

  const positionKey =
    motorIndex !== undefined
      ? `MultiMotor_N${motorIndex}_positionUserUnits`
      : 'positionSensorReading'

  const speedKey =
    motorIndex !== undefined ? `MultiMotor_N${motorIndex}_velocityActualValue` : 'motorSpeed'

  const fetchTelemetry = useMemo(
    () =>
      throttle(async (latestTimeMs: number) => {
        try {
          // FW completely overrides the packet values for the following (i.e. batteryCurrent gets filled with olPhase)
          const [frequency, clMagnitude, clPhase, olMagnitude, olPhase, speed, position] =
            await Promise.all([
              packetThreadPoolApi.getLatestTelemValueNotAfter('sysidFrequency', latestTimeMs),
              packetThreadPoolApi.getLatestTelemValueNotAfter('sysidMagnitude', latestTimeMs),
              packetThreadPoolApi.getLatestTelemValueNotAfter('motorCurrent', latestTimeMs),
              packetThreadPoolApi.getLatestTelemValueNotAfter('batteryVoltage', latestTimeMs),
              packetThreadPoolApi.getLatestTelemValueNotAfter('batteryCurrent', latestTimeMs),
              // these two values however, are per-motor and have their proper respective values still in here
              packetThreadPoolApi.getLatestTelemValueNotAfter(speedKey, latestTimeMs),
              packetThreadPoolApi.getLatestTelemValueNotAfter(positionKey, latestTimeMs)
            ])

          setTelemetry({
            excitationFrequency: toNumber(frequency),
            closedLoopMagnitude: toNumber(clMagnitude),
            closedLoopPhase: toNumber(clPhase),
            openLoopMagnitude: toNumber(olMagnitude),
            openLoopPhase: toNumber(olPhase),
            motorSpeed: toNumber(speed),
            motorPosition: toNumber(position)
          })
        } catch (e) {
          console.warn('Failed to fetch SysID telemetry:', e)
        }
      }, REFRESH_MS),
    [speedKey, positionKey]
  )

  useEffect(() => {
    const latestTimeMs = timeWindow?.[1]
    if (latestTimeMs === undefined) {
      return
    }
    fetchTelemetry(latestTimeMs)
  }, [timeWindow, fetchTelemetry])

  return telemetry
}

/**
 * Frequency Response Plot Component
 */
type PlotYKey = 'closedLoopMagnitude' | 'closedLoopPhase' | 'openLoopMagnitude' | 'openLoopPhase'

interface FrequencyResponsePlotProps {
  dataPoints: FrequencyDataPoint[]
  width: number
  height: number
  title: string
  yLabel: string
  yKey: PlotYKey
  color: string
}

const FrequencyResponsePlot = ({
  dataPoints,
  width,
  height,
  title,
  yLabel,
  yKey,
  color
}: FrequencyResponsePlotProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const isPhase = yKey.includes('Phase')

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const canvasContext = canvas.getContext('2d')
    if (!canvasContext) return

    // Background
    canvasContext.fillStyle = Colors.DARK_GREEN
    canvasContext.fillRect(0, 0, width, height)

    const plotWidth = width - PLOT_PADDING.left - PLOT_PADDING.right
    const plotHeight = height - PLOT_PADDING.top - PLOT_PADDING.bottom

    // Grid
    canvasContext.strokeStyle = '#444'
    canvasContext.lineWidth = 1

    const frequencies = dataPoints.map((p) => p.frequency)
    const yValues = dataPoints.map((p) => p[yKey])

    const maxFreq = Math.max(110, ...frequencies)
    const minY = isPhase ? Math.min(-180, ...yValues) : 0
    const maxY = isPhase ? Math.max(0, ...yValues) : Math.max(2, ...yValues)
    const yRange = maxY - minY

    // Vertical grid lines
    const freqSteps = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]
    canvasContext.beginPath()
    freqSteps.forEach((freq) => {
      const x = PLOT_PADDING.left + (freq / maxFreq) * plotWidth
      canvasContext.moveTo(x, PLOT_PADDING.top)
      canvasContext.lineTo(x, height - PLOT_PADDING.bottom)
    })
    canvasContext.stroke()

    // Horizontal grid lines
    const yStepCount = 5
    canvasContext.beginPath()
    for (let i = 0; i <= yStepCount; i++) {
      const y = PLOT_PADDING.top + (i / yStepCount) * plotHeight
      canvasContext.moveTo(PLOT_PADDING.left, y)
      canvasContext.lineTo(width - PLOT_PADDING.right, y)
    }
    canvasContext.stroke()

    // Axes
    canvasContext.strokeStyle = Colors.WHITE
    canvasContext.lineWidth = 2
    canvasContext.beginPath()
    canvasContext.moveTo(PLOT_PADDING.left, PLOT_PADDING.top)
    canvasContext.lineTo(PLOT_PADDING.left, height - PLOT_PADDING.bottom)
    canvasContext.lineTo(width - PLOT_PADDING.right, height - PLOT_PADDING.bottom)
    canvasContext.stroke()

    // Labels
    canvasContext.fillStyle = Colors.WHITE
    canvasContext.font = '11px sans-serif'
    canvasContext.textAlign = 'center'

    // X-axis labels
    freqSteps.forEach((freq) => {
      const x = PLOT_PADDING.left + (freq / maxFreq) * plotWidth
      canvasContext.fillText(freq.toString(), x, height - PLOT_PADDING.bottom + 12)
    })
    canvasContext.fillText('Frequency (Hz)', width / 2, height - 3)

    // Y-axis labels
    canvasContext.textAlign = 'right'
    for (let i = 0; i <= yStepCount; i++) {
      const y = PLOT_PADDING.top + (i / yStepCount) * plotHeight
      const value = maxY - (i / yStepCount) * yRange
      canvasContext.fillText(value.toFixed(isPhase ? 0 : 2), PLOT_PADDING.left - 5, y + 4)
    }

    // Y-axis title
    canvasContext.save()
    canvasContext.translate(12, height / 2)
    canvasContext.rotate(-Math.PI / 2)
    canvasContext.textAlign = 'center'
    canvasContext.fillText(yLabel, 0, 0)
    canvasContext.restore()

    // Title
    canvasContext.textAlign = 'center'
    canvasContext.font = 'bold 13px sans-serif'
    canvasContext.fillText(title, width / 2, 14)

    // Data points and lines
    if (dataPoints.length > 0) {
      canvasContext.strokeStyle = color
      canvasContext.lineWidth = 2
      canvasContext.beginPath()

      const sortedPoints = [...dataPoints].sort((a, b) => a.frequency - b.frequency)

      sortedPoints.forEach((point, index) => {
        const x = PLOT_PADDING.left + (point.frequency / maxFreq) * plotWidth
        const y = PLOT_PADDING.top + ((maxY - point[yKey]) / yRange) * plotHeight

        if (index === 0) {
          canvasContext.moveTo(x, y)
        } else {
          canvasContext.lineTo(x, y)
        }
      })
      canvasContext.stroke()

      // Draw points
      canvasContext.fillStyle = color
      sortedPoints.forEach((point) => {
        const x = PLOT_PADDING.left + (point.frequency / maxFreq) * plotWidth
        const y = PLOT_PADDING.top + ((maxY - point[yKey]) / yRange) * plotHeight

        canvasContext.beginPath()
        canvasContext.arc(x, y, 3, 0, 2 * Math.PI)
        canvasContext.fill()
      })
    }
  }, [dataPoints, width, height, title, yLabel, yKey, color, isPhase])

  return <canvas ref={canvasRef} width={width} height={height} style={{ borderRadius: 4 }} />
}

export const SysIDScreen = (props: INavigationProps) => {
  const { connectedDevice } = useDevices()
  const subActuators = useSubActuators()
  const { next, prev } = props
  // SysID overlay fields are board-level (REALTIME_DATA). Per-motor reads
  // (position/speed) follow the commanded motor so the screen always shows
  // the motor under control — falling back to the SysID-running motor or 0.
  const [sysIdRequestedMotor, setSysIdRequestedMotor] = useState<number | undefined>(undefined)
  const [enabledMotor, setEnabledMotor] = useState<number | undefined>(undefined)
  const activeMotorIndex = enabledMotor ?? sysIdRequestedMotor
  const sysIdTelemetry = useSysIDTelemetry(activeMotorIndex)

  // Safe access to device state
  const isConnected = connectedDevice !== undefined && connectedDevice.initialized
  const latestConfig = isConnected ? connectedDevice.inputConfig : undefined
  const controlMode = isConnected ? connectedDevice.controlMode : undefined
  const isUsbMode = latestConfig?.inputMode === InputMode.USB
  const isPositionMode = controlMode === ControlMode.POSITION
  const deviceHandle = isConnected ? connectedDevice.handle : undefined

  // Get position limits from device config directly (useThrottleConfig crashes this screen)
  // For POSITION mode: slider range = [IP_Lower, IP_Upper], value sent directly (1:1)
  const inputLower = latestConfig?.inputLimitLower ?? 0
  const inputCenter = latestConfig?.inputLimitCenter ?? 2000
  const inputUpper = latestConfig?.inputLimitUpper ?? 4000
  const stepSize = 1

  // Throttle state - default to center position
  const [throttleValue, _setThrottleValue] = useState<number>(inputCenter)
  const throttleValueRef = useRef<number>(inputCenter)
  const [manualValueStr, setManualValueStr] = useState<string>('')
  const pauseThrottleRef = useRef<boolean>(document.hidden)

  // Enable/disable state per motor for position command (declared above to feed
  // useSysIDTelemetry's motor index)
  const isEnabledRef = useRef<boolean>(false)

  // Collected frequency response data
  const [dataPoints, setDataPoints] = useState<FrequencyDataPoint[]>([])
  const lastFrequencyRef = useRef<number | undefined>(undefined)

  const setThrottleValue = useMemo(
    () => throttle(_setThrottleValue, REFRESH_MS),
    [_setThrottleValue]
  )

  // Update throttle to center when config changes (device connects)
  useEffect(() => {
    if (latestConfig) {
      const center = latestConfig.inputLimitCenter ?? 2000
      _setThrottleValue(center)
      throttleValueRef.current = center
    }
  }, [latestConfig])

  // Visibility change handler
  useEffect(() => {
    const onVisibilityChange = () => {
      pauseThrottleRef.current = document.hidden
    }
    window.addEventListener('visibilitychange', onVisibilityChange)
    return () => window.removeEventListener('visibilitychange', onVisibilityChange)
  }, [])

  // Update ref when throttle value changes
  useEffect(() => {
    throttleValueRef.current = throttleValue
  }, [throttleValue])

  // Send position command pulses only when enabled (like Position widget)
  // For POSITION mode: send throttle value directly (1:1 correspondence per physicalToDevice), to the selected motor
  const sendPulse = useCallback(() => {
    if (enabledMotor === undefined || !deviceHandle || !latestConfig || pauseThrottleRef.current) {
      return
    }

    // Send position command to the enabled motor
    hidSend(
      deviceHandle,
      serializeSetUsbThrottleControlRequest({
        value: throttleValueRef.current,
        motorIndex: enabledMotor ?? 0
      }) as BufferSource
    ).catch((e) => console.warn('Failed to send position command:', e))
  }, [deviceHandle, latestConfig, enabledMotor])

  // Command sending interval - only runs when enabled
  useEffect(() => {
    if (enabledMotor === undefined) {
      return
    }
    isEnabledRef.current = true
    sendPulse()
    const interval = setInterval(sendPulse, PULSE_INTERVAL_MS)
    return () => {
      isEnabledRef.current = false
      clearInterval(interval)
    }
  }, [sendPulse, enabledMotor])

  // Throttle onChange handler
  const onThrottleChange = useCallback(
    (value: number) => {
      setThrottleValue(value)
    },
    [setThrottleValue]
  )

  // Manual value submit handler
  const updateWithManualValue = useCallback(() => {
    const manualValue = parseFloat(manualValueStr)
    if (!isFinite(manualValue)) {
      return
    }
    // Clamp to [inputLower, inputUpper]
    const value = Math.max(Math.min(manualValue, inputUpper), inputLower)
    setThrottleValue(value)
    setManualValueStr('')
  }, [manualValueStr, setThrottleValue, inputLower, inputUpper])

  // Collect data points as frequency changes
  useEffect(() => {
    const freq = sysIdTelemetry.excitationFrequency
    const clMag = sysIdTelemetry.closedLoopMagnitude
    const clPhase = sysIdTelemetry.closedLoopPhase
    const olMag = sysIdTelemetry.openLoopMagnitude
    const olPhase = sysIdTelemetry.openLoopPhase

    if (freq === undefined || clMag === undefined || clPhase === undefined) return
    if (freq <= 0) return

    const lastFreq = lastFrequencyRef.current
    if (lastFreq !== undefined && Math.abs(freq - lastFreq) < 0.1) return

    setDataPoints((prev) => {
      const existingIndex = prev.findIndex((p) => Math.abs(p.frequency - freq) < 0.5)

      const newPoint: FrequencyDataPoint = {
        frequency: freq,
        closedLoopMagnitude: clMag,
        closedLoopPhase: clPhase,
        openLoopMagnitude: olMag ?? 0,
        openLoopPhase: olPhase ?? 0,
        timestamp: Date.now()
      }

      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = newPoint
        return updated
      } else {
        return [...prev, newPoint]
      }
    })

    lastFrequencyRef.current = freq
  }, [
    sysIdTelemetry.excitationFrequency,
    sysIdTelemetry.closedLoopMagnitude,
    sysIdTelemetry.closedLoopPhase,
    sysIdTelemetry.openLoopMagnitude,
    sysIdTelemetry.openLoopPhase
  ])

  const clearData = useCallback(() => {
    setDataPoints([])
    lastFrequencyRef.current = undefined
  }, [])

  const downloadCsv = useCallback(() => {
    if (dataPoints.length === 0) return

    // Sort by frequency
    const sortedData = [...dataPoints].sort((a, b) => a.frequency - b.frequency)

    // Create CSV content
    const headers = [
      'Frequency (Hz)',
      'Closed Loop Magnitude',
      'Closed Loop Phase (deg)',
      'Open Loop Magnitude',
      'Open Loop Phase (deg)'
    ]
    const csvRows = [headers.join(',')]

    sortedData.forEach((point) => {
      csvRows.push(
        [
          point.frequency.toFixed(4),
          point.closedLoopMagnitude.toFixed(6),
          point.closedLoopPhase.toFixed(4),
          point.openLoopMagnitude.toFixed(6),
          point.openLoopPhase.toFixed(4)
        ].join(',')
      )
    })

    const csvContent = csvRows.join('\n')
    const filename = filenameWithTimestamp('sysid_bode_data') + '.csv'
    downloadText(filename, csvContent)
  }, [dataPoints])

  // Start SysID frequency sweep on firmware

  // Start SysID frequency sweep on firmware for a specific motor index
  const startSysId = useCallback(
    (motorIndex?: number) => {
      if (!deviceHandle) {
        return
      }

      // Clear previous data points when starting a new sweep
      setDataPoints([])
      lastFrequencyRef.current = undefined
      setSysIdRequestedMotor(motorIndex)

      // Send START_SYSID command to firmware with motorIndex
      const dataView = serializeStartSysidRequest({ motorIndex: motorIndex ?? 0 })
      hidSend(deviceHandle, dataView.buffer)
    },
    [deviceHandle]
  )

  // Stop SysID frequency sweep on firmware
  const stopSysId = useCallback(() => {
    if (!deviceHandle) {
      return
    }

    setSysIdRequestedMotor(undefined)

    // Send STOP_SYSID command to firmware
    const dataView = serializeStopSysidRequest({})
    hidSend(deviceHandle, dataView.buffer)
  }, [deviceHandle])

  // Detect when SysID completes (frequency goes to 0 or undefined while we requested it)
  useEffect(() => {
    if (sysIdRequestedMotor !== undefined) {
      const freq = sysIdTelemetry.excitationFrequency
      // If frequency is 0 or undefined and we have data, sweep completed
      if ((freq === undefined || freq === 0) && dataPoints.length > 0) {
        setSysIdRequestedMotor(undefined)
      }
    }
  }, [sysIdTelemetry.excitationFrequency, sysIdRequestedMotor, dataPoints.length])

  const formatValue = (value: number | undefined, decimals: number = 4): string => {
    if (value === undefined) return '---'
    return value.toFixed(decimals)
  }

  // SysID is running if user requested it OR firmware reports active frequency
  const isRunning =
    sysIdRequestedMotor !== undefined ||
    (sysIdTelemetry.excitationFrequency !== undefined && sysIdTelemetry.excitationFrequency > 0)

  // Overlay fields (motorCurrent/batteryVoltage/batteryCurrent on REALTIME_DATA)
  // carry their normal values when SysID is off — hide them in the SysID readout
  // so we don't display things like "28 A" labelled as "phase".
  const displayed = isRunning
    ? sysIdTelemetry
    : {
        ...sysIdTelemetry,
        excitationFrequency: undefined,
        closedLoopMagnitude: undefined,
        closedLoopPhase: undefined,
        openLoopMagnitude: undefined,
        openLoopPhase: undefined
      }

  // Show unavailable message for non-position control modes
  if (!isPositionMode && isConnected) {
    return (
      <div style={configContainerStyle}>
        <div style={screenTitleStyle}>System Identification</div>
        <div
          style={{
            backgroundColor: '#4a3c1c',
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
            textAlign: 'center'
          }}
        >
          <div style={{ color: '#ffcc00', fontSize: 16, fontWeight: 'bold', marginBottom: 8 }}>
            Unavailable
          </div>
          <div style={{ color: Colors.WHITE, fontSize: 14 }}>
            System Identification is only available in Position control mode.
            <br />
            Please change the Control Mode to Position in the Throttle Settings screen and flash the
            configuration to the device.
          </div>
        </div>
        {/* Navigation buttons */}
        <div style={{ display: 'flex', width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.WHITE}
              textColor="#000000"
              onClick={prev}
              disabled={prev === undefined}
            >
              Back
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.BUTTON_GREEN}
              textColor={Colors.WHITE}
              onClick={next}
              disabled={next === undefined}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={configContainerStyle}>
      <div style={screenTitleStyle}>System Identification</div>

      <div
        style={{
          color: Colors.WHITE,
          marginBottom: 12,
          paddingLeft: 8,
          paddingRight: 8,
          maxWidth: PLOT_WIDTH,
          textAlign: 'center',
          fontSize: 13
        }}
      >
        Frequency sweep (1-100 Hz) to characterize closed-loop position control response.
      </div>

      {/* Status indicator */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 12,
          padding: '6px 12px',
          backgroundColor: isRunning
            ? Colors.BUTTON_GREEN
            : isConnected
              ? Colors.DARK_GREEN
              : '#4a1c1c',
          borderRadius: 4
        }}
      >
        <div
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: isRunning ? '#00ff00' : isConnected ? '#ffcc00' : '#ff4444',
            marginRight: 8
          }}
        />
        <span style={{ color: Colors.WHITE, fontWeight: 'bold', fontSize: 13 }}>
          {isRunning
            ? `Running - ${formatValue(sysIdTelemetry.excitationFrequency, 1)} Hz`
            : isConnected
              ? 'Ready'
              : 'No Device Connected'}
        </span>
      </div>

      {/* Main content grid */}
      <div
        style={{
          display: 'flex',
          gap: 16,
          marginBottom: 12,
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}
      >
        {/* Left column: Throttle Control (Dashboard-style) */}
        <div
          style={{
            backgroundColor: Colors.DARK_GREEN,
            padding: 12,
            borderRadius: 8,
            minWidth: 280,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              color: Colors.WHITE,
              fontWeight: 'bold',
              marginBottom: 10,
              fontSize: 14,
              borderBottom: '1px solid white',
              paddingBottom: 6,
              width: '100%',
              textAlign: 'center'
            }}
          >
            Position Command
          </div>

          {!isUsbMode && (
            <div style={{ color: '#ffcc00', fontSize: 11, marginBottom: 8 }}>
              Set Input Mode to USB to enable position control
            </div>
          )}

          {/* Start/Stop Buttons for each motor */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <BPButton
              large
              intent={enabledMotor === 0 ? 'danger' : 'success'}
              onClick={() => {
                if (enabledMotor === 0) {
                  setEnabledMotor(undefined)
                } else {
                  setEnabledMotor(0)
                }
              }}
              disabled={
                !isConnected || !isUsbMode || (enabledMotor !== undefined && enabledMotor !== 0)
              }
              style={{ minWidth: 120 }}
            >
              {enabledMotor === 0 ? 'Stop (Motor 0)' : 'Start (Motor 0)'}
            </BPButton>
            {Object.keys(subActuators).length > 1 && (
              <BPButton
                large
                intent={enabledMotor === 1 ? 'danger' : 'success'}
                onClick={() => {
                  if (enabledMotor === 1) {
                    setEnabledMotor(undefined)
                  } else {
                    setEnabledMotor(1)
                  }
                }}
                disabled={
                  !isConnected || !isUsbMode || (enabledMotor !== undefined && enabledMotor !== 1)
                }
                style={{ minWidth: 120 }}
              >
                {enabledMotor === 1 ? 'Stop (Motor 1)' : 'Start (Motor 1)'}
              </BPButton>
            )}
          </div>

          {/* Throttle with vertical slider and spinner (Dashboard-style) */}
          <div style={{ display: 'flex', alignItems: 'center', height: 200, gap: 16 }}>
            {/* Vertical Slider */}
            <div style={{ height: '100%' }}>
              <Slider
                className="throttle-slider"
                min={inputLower}
                max={inputUpper}
                value={throttleValue}
                onChange={onThrottleChange}
                labelValues={[inputLower, inputCenter, inputUpper]}
                vertical={true}
                showTrackFill={false}
                stepSize={stepSize}
              />
            </div>

            {/* Spinner and Input */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative', width: 100, height: 100 }}>
                <Spinner
                  value={(throttleValue - inputLower) / Math.max(inputUpper - inputLower, 1)}
                  intent={throttleValue < inputCenter ? Intent.DANGER : Intent.SUCCESS}
                  size={100}
                  style={{
                    transform: 'rotate(180deg)',
                    scale: throttleValue < inputCenter ? '-1 1' : undefined
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: Colors.WHITE,
                    fontFamily: 'monospace',
                    fontSize: 14,
                    fontWeight: 'bold'
                  }}
                >
                  {throttleValue.toFixed(0)}
                </div>
              </div>

              <NumericInput
                placeholder={throttleValue.toString()}
                min={inputLower}
                max={inputUpper}
                style={{
                  width: 80,
                  borderRadius: 8,
                  backgroundColor: Colors.LIGHT_GREEN,
                  color: Colors.WHITE
                }}
                clampValueOnBlur={true}
                onValueChange={(_val, valStr) => setManualValueStr(valStr)}
                value={manualValueStr}
                rightElement={
                  <BPButton
                    icon="arrow-right"
                    minimal={true}
                    intent="success"
                    onClick={updateWithManualValue}
                  />
                }
                buttonPosition="none"
                onKeyDown={(e) => (e.key === 'Enter' ? updateWithManualValue() : undefined)}
              />

              <BPButton intent="primary" onClick={() => setThrottleValue(inputCenter)} small>
                Center
              </BPButton>
            </div>
          </div>

          <div
            style={{
              marginTop: 10,
              paddingTop: 8,
              borderTop: '1px solid #444',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              width: '100%'
            }}
          >
            <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Current Position:</span>
            <span style={{ color: '#00ff00', fontFamily: 'monospace' }}>
              {formatValue(sysIdTelemetry.motorPosition, 2)} {getPositionUnitLabel()}
            </span>
          </div>
        </div>

        {/* Right column: Telemetry */}
        <div
          style={{
            backgroundColor: Colors.DARK_GREEN,
            padding: 12,
            borderRadius: 8,
            minWidth: 240
          }}
        >
          <div
            style={{
              color: Colors.WHITE,
              fontWeight: 'bold',
              marginBottom: 10,
              fontSize: 14,
              borderBottom: '1px solid white',
              paddingBottom: 6
            }}
          >
            SysID Telemetry
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Frequency:</span>
              <span style={{ color: '#00ff00', fontFamily: 'monospace' }}>
                {formatValue(displayed.excitationFrequency, 2)} Hz
              </span>
            </div>

            <div
              style={{
                marginTop: 4,
                paddingTop: 4,
                borderTop: '1px solid #333',
                fontWeight: 'bold',
                color: '#00ff00'
              }}
            >
              Closed Loop
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Magnitude:</span>
              <span style={{ color: '#00ff00', fontFamily: 'monospace' }}>
                {formatValue(displayed.closedLoopMagnitude, 4)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Phase:</span>
              <span style={{ color: '#ffff00', fontFamily: 'monospace' }}>
                {formatValue(displayed.closedLoopPhase, 2)} deg
              </span>
            </div>

            <div
              style={{
                marginTop: 4,
                paddingTop: 4,
                borderTop: '1px solid #333',
                fontWeight: 'bold',
                color: '#ff8800'
              }}
            >
              Open Loop
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Magnitude:</span>
              <span style={{ color: '#ff8800', fontFamily: 'monospace' }}>
                {formatValue(displayed.openLoopMagnitude, 4)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Phase:</span>
              <span style={{ color: '#ff00ff', fontFamily: 'monospace' }}>
                {formatValue(displayed.openLoopPhase, 2)} deg
              </span>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 4,
                paddingTop: 4,
                borderTop: '1px solid #444'
              }}
            >
              <span style={{ color: Colors.WHITE, opacity: 0.8 }}>Data Points:</span>
              <span style={{ color: Colors.WHITE, fontFamily: 'monospace' }}>
                {dataPoints.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Frequency Response Plots - 2x2 Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          marginBottom: 12
        }}
      >
        {/* Closed Loop Plots */}
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Closed Loop Magnitude"
          yLabel="Magnitude"
          yKey="closedLoopMagnitude"
          color="#00ff00"
        />
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Closed Loop Phase"
          yLabel="Phase (deg)"
          yKey="closedLoopPhase"
          color="#ffff00"
        />

        {/* Open Loop Plots */}
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Open Loop Magnitude"
          yLabel="Magnitude"
          yKey="openLoopMagnitude"
          color="#ff8800"
        />
        <FrequencyResponsePlot
          dataPoints={dataPoints}
          width={PLOT_WIDTH / 2 + 50}
          height={PLOT_HEIGHT}
          title="Open Loop Phase"
          yLabel="Phase (deg)"
          yKey="openLoopPhase"
          color="#ff00ff"
        />
      </div>

      {/* Control buttons */}

      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {/* Start buttons for each motor */}
        <Button
          backgroundColor={sysIdRequestedMotor === 0 ? '#cc3333' : '#0066cc'}
          textColor={Colors.WHITE}
          onClick={sysIdRequestedMotor === 0 ? stopSysId : () => startSysId(0)}
          disabled={!isConnected || (isRunning && sysIdRequestedMotor !== 0)}
        >
          {sysIdRequestedMotor === 0 ? 'Stop SysID' : 'Start SysID (Motor 0)'}
        </Button>
        {Object.keys(subActuators).length > 1 && (
          <Button
            backgroundColor={sysIdRequestedMotor === 1 ? '#cc3333' : '#0066cc'}
            textColor={Colors.WHITE}
            onClick={sysIdRequestedMotor === 1 ? stopSysId : () => startSysId(1)}
            disabled={!isConnected || (isRunning && sysIdRequestedMotor !== 1)}
          >
            {sysIdRequestedMotor === 1 ? 'Stop SysID' : 'Start SysID (Motor 1)'}
          </Button>
        )}
        <Button backgroundColor="#aa3333" textColor={Colors.WHITE} onClick={clearData}>
          Clear Data
        </Button>
        <Button
          backgroundColor={Colors.BUTTON_GREEN}
          textColor={Colors.WHITE}
          onClick={downloadCsv}
          disabled={dataPoints.length === 0}
        >
          Download CSV
        </Button>
      </div>

      {/* Navigation buttons */}
      <div style={{ display: 'flex', width: FORM_WIDTH }}>
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.WHITE}
            textColor="#000000"
            onClick={prev}
            disabled={prev === undefined}
          >
            Back
          </Button>
        </div>
        <div style={{ width: 8 }} />
        <div style={{ flex: 1 }}>
          <Button
            backgroundColor={Colors.BUTTON_GREEN}
            textColor={Colors.WHITE}
            onClick={next}
            disabled={next === undefined}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}
